import { useEffect, useState } from 'react';
import { ThemeProps } from '../utils/theme';
import { getClarityIdByName } from '../services/clarityService';

interface ClarityAnalyticsProps {
  theme?: ThemeProps;
}

declare global {
  interface Window {
    clarity: any;
    CookieConsent: any;
  }
}

const ClarityAnalytics: React.FC<ClarityAnalyticsProps> = ({ theme }) => {
  const [clarityId, setClarityId] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const fetchClarityId = async () => {
      let finalClarityId = 'rqloengqf3'; // Default fallback
      
      if (theme?.name) {
        try {
          finalClarityId = await getClarityIdByName(theme.name);
        } catch (error) {
          console.error('Failed to fetch Clarity ID, using fallback');
        }
      }
      
      setClarityId(finalClarityId);
    };

    fetchClarityId();
  }, [theme?.name]);

  useEffect(() => {
    // Don't initialize if we don't have a clarity ID or already initialized
    if (!clarityId || isInitialized) {
      return;
    }

    // Check if analytics consent is given
    const checkConsent = () => {
      // Check if vanilla-cookieconsent is available and analytics is accepted
      if (typeof window !== 'undefined' && window.CookieConsent) {
        try {
          const prefs = window.CookieConsent.getUserPreferences();
          return prefs && prefs.acceptedCategories && prefs.acceptedCategories.includes('analytics');
        } catch (error) {
          console.error('Error checking cookie preferences:', error);
          return false;
        }
      }
      
      // If no cookie consent system, don't load by default (GDPR compliance)
      return false;
    };

    // Function to initialize Clarity
    const initializeClarity = () => {
      // Check if Clarity script is already loaded
      const existingScript = document.querySelector(`script[src*="clarity.ms/tag/${clarityId}"]`);
      if (existingScript) {
        setIsInitialized(true);
        
        // Still set the theme name even if script exists
        if (theme?.name && window.clarity) {
          window.clarity('set', 'siteName', theme.name);
        }
        return;
      }

      // Inject Clarity tracking script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.clarity.ms/tag/${clarityId}`;
      script.onload = () => {
        setIsInitialized(true);
        
        // Set custom tags after script loads
        if (theme?.name) {
          setTimeout(() => {
            if (window.clarity) {
              // Set primary site identifier
              window.clarity('set', 'siteName', theme.name);
              
              // Optional: Set additional useful data
              if (theme.website) {
                window.clarity('set', 'websiteUrl', theme.website);
              }
              
              // Set environment info
              window.clarity('set', 'environment', window.location.hostname);
            }
          }, 500);
        }
      };
      script.onerror = () => {
        console.error('Failed to load Clarity script');
      };
      
      document.head.appendChild(script);

      // Define Clarity function immediately
      window.clarity = window.clarity || function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
    };

    // Check consent periodically until cookie consent system is available
    const consentCheckInterval = setInterval(() => {
      if (checkConsent()) {
        clearInterval(consentCheckInterval);
        console.log('Analytics consent granted, initializing Clarity');
        initializeClarity();
      }
    }, 1000);

    // Also check immediately
    if (checkConsent()) {
      clearInterval(consentCheckInterval);
      console.log('Analytics consent already granted, initializing Clarity');
      initializeClarity();
    }

    // Clean up interval after 30 seconds to prevent infinite checking
    const timeoutId = setTimeout(() => {
      clearInterval(consentCheckInterval);
      console.log('Stopped checking for analytics consent');
    }, 30000);

    // Cleanup function
    return () => {
      clearInterval(consentCheckInterval);
      clearTimeout(timeoutId);
    };

  }, [clarityId, theme?.name, theme?.website, isInitialized]);

  return null;
};

export default ClarityAnalytics;